import React from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {ConfigProvider, theme, App as AntApp} from 'antd';
import {HelmetProvider} from 'react-helmet-async';
import {authService} from './services/api';
import {PageTitleProvider} from './context/PageTitleContext';
import {ThemeProvider, useTheme} from './context/ThemeContext';

// Layouts
import MainLayout from './components/layout/MainLayout';

// Pages
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import UsersList from './pages/UsersList';
import UserDetails from './pages/UserDetails';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import UserEdit from './pages/UserEdit';
import UserCreate from './pages/UserCreate';
import SecurityPage from "./pages/SecurityPage";
import TemporaryPasswordPage from './pages/TemporaryPasswordPage';
import TwoFactorAuthPage from './pages/TwoFactorAuthPage';
import OAuthAuthorizePage from "./pages/OAuthAuthorizePage";
import AuthAttemptsLogs from "./pages/AuthAttemptsLogs";
import AuthBlockLogs from "./pages/AuthBlockLogs";

// Защищенный маршрут, проверяющий авторизацию и необходимость смены временного пароля
const ProtectedRoute = ({children, adminRequired = false}) => {
    const isLoggedIn = authService.isAuthenticated();
    const hasTemporaryPassword = authService.hasTemporaryPassword();
    const currentUser = authService.getCurrentUser();

    if (!isLoggedIn) {
        return <Navigate to="/login"/>;
    }

    // Если у пользователя временный пароль и он не на странице смены пароля
    if (hasTemporaryPassword) {
        return <Navigate to="/change-temporary-password"/>;
    }

    // Если требуется роль admin и текущий пользователь не admin
    if (adminRequired && currentUser?.role !== 'admin') {
        return <Navigate to="/"/>;
    }

    return children;
};

// Защищенный маршрут только для страницы смены временного пароля
const TemporaryPasswordRoute = ({children}) => {
    const isLoggedIn = authService.isAuthenticated();
    const hasTemporaryPassword = authService.hasTemporaryPassword();

    if (!isLoggedIn) {
        return <Navigate to="/login"/>;
    }

    // Если нет временного пароля, то перенаправляем на главную
    if (!hasTemporaryPassword) {
        return <Navigate to="/"/>;
    }

    return children;
};

// Компонент для применения выбранной темы
const ThemedApp = () => {
    const {darkMode} = useTheme();

    return (
        <HelmetProvider>
            <ConfigProvider
                theme={{
                    token: darkMode
                        ? {
                            colorPrimary: '#05D32A',
                            borderRadius: 10,
                            colorBgBase: '#262726'
                        }
                        : {
                            colorPrimary: '#05D32A',
                            borderRadius: 10
                        },
                    algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                }}
                componentSize="large"
            >
                {/* Добавляем AntApp для обеспечения контекста сообщений */}
                <AntApp>
                    <PageTitleProvider>
                        <Router>
                            <Routes>
                                {/* Страница входа */}
                                <Route path="/login" element={
                                    authService.isAuthenticated() ?
                                        (authService.hasTemporaryPassword() ?
                                            <Navigate to="/change-temporary-password"/> :
                                            <Navigate to="/"/>) :
                                        <LoginPage/>
                                }/>

                                {/* Страница ввода 2fa */}
                                <Route path="/two-factor-auth" element={
                                        authService.isPending2FA() ?
                                            <TwoFactorAuthPage/> :
                                            <Navigate to="/login" replace/>
                                    }
                                />

                                {/* Страница смены временного пароля */}
                                <Route path="/change-temporary-password" element={
                                    <TemporaryPasswordRoute>
                                        <TemporaryPasswordPage/>
                                    </TemporaryPasswordRoute>
                                }/>

                                {/* Страница OAuth авторизации */}
                                <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />

                                {/* Основной лейаут с вложенными маршрутами */}
                                <Route path="/" element={<MainLayout/>}>

                                    {/* Профиль пользователя */}
                                    <Route index element={
                                        <ProtectedRoute>
                                            <ProfilePage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница Безопастность у пользователя */}
                                    <Route path="security" element={
                                        <ProtectedRoute>
                                            <SecurityPage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страницы администрирования */}
                                    <Route path="users" element={
                                        <ProtectedRoute adminRequired={true}>
                                            <UsersList/>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="users/:id" element={
                                        <ProtectedRoute adminRequired={true}>
                                            <UserDetails/>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="users/:id/edit" element={
                                        <ProtectedRoute adminRequired={true}>
                                            <UserEdit/>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="users/create" element={
                                        <ProtectedRoute adminRequired={true}>
                                            <UserCreate/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница логов входа в аккаунты */}
                                    <Route path="auth-attempts-logs" element={
                                        <ProtectedRoute adminRequired={true}>
                                            <AuthAttemptsLogs/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница логов блокировок ip or login */}
                                    <Route path="auth-block-logs" element={
                                        <ProtectedRoute adminRequired={true}>
                                            <AuthBlockLogs/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* 404 ошибка */}
                                    <Route path="*" element={<ErrorPage
                                        statusCode={404}
                                        title="Страница не найдена"
                                        description="Такой страницы не существует"
                                    />}/>

                                    {/* Для отображения ошибок с сервера */}
                                    <Route path="/error" element={<ErrorPage
                                        statusCode={500}
                                        title="Ошибка сервера"
                                        description="Произошла ошибка. Попробуйте обновить страницу или вернуться позже."
                                    />}/>

                                    {/* Для ошибки доступа */}
                                    <Route path="/forbidden" element={<ErrorPage
                                        statusCode={403}
                                        title="Доступ запрещен"
                                        description="У вас недостаточно прав для просмотра данной страницы"
                                    />}/>
                                </Route>
                            </Routes>
                        </Router>
                    </PageTitleProvider>
                </AntApp>
            </ConfigProvider>
        </HelmetProvider>
    );
};

function App() {
    return (
        <ThemeProvider>
            <ThemedApp/>
        </ThemeProvider>
    );
}

export default App;